// This is a copy of expression-utils.ts in the api project

function computeContextExpression(
  contextPath: string,
  context: Record<string, unknown>,
  contextExpressionRoot: string,
) {
  // Format: {contextExpressionRoot}.{contextPath} where contextPath is a dot-separated path
  const contextPathParts = contextPath.split('.');
  let currentContext: Record<string, unknown> | unknown = context;

  for (const key of contextPathParts) {
    // The context may not contain the path. In that case, return undefined.
    if (currentContext === undefined || currentContext === null) {
      return undefined;
    }

    if (typeof currentContext !== 'object') {
      throw new Error(
        `Invalid expression format expression='${contextExpressionRoot}.${contextPath}'`,
      );
    }
    currentContext = (currentContext as Record<string, unknown>)[key];
  }

  return currentContext;
}

function computeFunctionExpression(expression: string) {
  // Expected expression format: {functionName}(jsonPayloadStr)
  const match = expression.match(/([a-zA-Z0-9_]+)\((.+)\)/);
  if (!match) {
    throw new Error(`Invalid expression format expression='${expression}'`);
  }

  const [, functionName, jsonPayloadStr] = match;
  let jsonPayload;
  try {
    jsonPayload = JSON.parse(jsonPayloadStr);
  } catch (error) {
    console.error(
      `Failed to parse JSON payload from expression='${expression}': ${error}`,
    );
    throw new Error(`Invalid JSON payload in expression='${expression}'`);
  }

  // Handle different function types
  let secs: number;
  switch (functionName) {
    case 'after':
      if (typeof jsonPayload !== 'object') {
        throw new Error(
          `Invalid payload for 'after' function in expression='${expression}'`,
        );
      }
      secs =
        jsonPayload['secs'] ?? jsonPayload['seconds'] ?? jsonPayload['sec'];
      if (typeof secs !== 'number') {
        throw new Error(
          `Invalid seconds value for 'after' function in expression='${expression}'`,
        );
      }
      // Return the current time plus the specified seconds
      return new Date(Date.now() + secs * 1000).toISOString();
    default:
      throw new Error(
        `Invalid functionName '${functionName}' in expression='${expression}'`,
      );
  }
}

function computeValueExpression(
  expression: string,
  context: Record<string, unknown>,
  contextExpressionRoot: string,
): string | Record<string, unknown> | unknown {
  const contextExpressionPrefix = `${contextExpressionRoot}.`;
  if (expression.startsWith(contextExpressionPrefix)) {
    const contextPath = expression.substring(contextExpressionPrefix.length);
    return computeContextExpression(
      contextPath,
      context,
      contextExpressionRoot,
    );
  }

  return computeFunctionExpression(expression);
}

function expandStringValue(
  value: string,
  context: Record<string, unknown>,
  contextExpressionRoot: string,
): string | Record<string, unknown> | unknown {
  const trimmedValue = value.trim();
  if (trimmedValue.startsWith('{{') && trimmedValue.endsWith('}}')) {
    const expression = trimmedValue
      .substring(2, trimmedValue.length - 2)
      .trim();
    return computeValueExpression(expression, context, contextExpressionRoot);
  }
  return value;
}

export function expandExpressions(
  unexpandedRecord: Record<string, unknown>,
  context: Record<string, unknown>,
  contextExpressionRoot: string,
) {
  const expandedRecord: Record<string, unknown> = {};

  for (const [key, origValue] of Object.entries(unexpandedRecord)) {
    if (typeof origValue === 'string') {
      expandedRecord[key] = expandStringValue(
        origValue,
        context,
        contextExpressionRoot,
      );
    } else if (Array.isArray(origValue)) {
      // Recursively expand array elements
      expandedRecord[key] = origValue.map((item) => {
        if (typeof item === 'string') {
          return expandStringValue(item, context, contextExpressionRoot);
        } else if (typeof item === 'object') {
          return expandExpressions(
            item as Record<string, unknown>,
            context,
            contextExpressionRoot,
          );
        } else {
          return item;
        }
      });
    } else if (typeof origValue === 'object' && origValue !== null) {
      // Recursively expand nested objects
      expandedRecord[key] = expandExpressions(
        origValue as Record<string, unknown>,
        context,
        contextExpressionRoot,
      );
    } else {
      expandedRecord[key] = origValue;
    }
  }

  return expandedRecord;
}
